import { useMutation, useQuery } from '@apollo/client';
import { confirm } from '@components/ConfirmModal/confirm';
import { DataTable } from '@components/DataTable';
import { DataTableColumn } from '@components/DataTable/types';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { PermissionOption } from '@components/upercut-permissions/PermissionSelector';
import RoleDialog from '@components/upercut-permissions/RoleDialog';
import {
    CREATE_UPERCUT_ROLE,
    DELETE_UPERCUT_ROLE,
    GET_UPERCUT_PERMISSIONS,
    GET_UPERCUT_ROLES,
    UPDATE_UPERCUT_ROLE
} from '@gql/upercut-permissions';
import { useDebounce } from '@hooks/useDebounce';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

interface RoleItem {
    _id: string;
    name: string;
    slug: string;
    description?: string;
    isSystemRole?: boolean;
    permissions: PermissionOption[];
}

const RoleManagement = () => {
    const { showSnackbar } = useSnackbar();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [editingRole, setEditingRole] = useState<RoleItem | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const debaouncedSearchQuery = useDebounce(searchQuery, 500);

    const { data: permissionsData } = useQuery(GET_UPERCUT_PERMISSIONS);
    const { data: rolesData, refetch: refetchRoles } = useQuery(GET_UPERCUT_ROLES, {
        variables: { filter: debaouncedSearchQuery ? { search: debaouncedSearchQuery } : undefined },
        fetchPolicy: 'cache-and-network',
    });

    const [createRole] = useMutation(CREATE_UPERCUT_ROLE);
    const [updateRole] = useMutation(UPDATE_UPERCUT_ROLE);
    const [deleteRole] = useMutation(DELETE_UPERCUT_ROLE);

    const permissions: PermissionOption[] = permissionsData?.upercutPermissions ?? [];
    const roles: RoleItem[] = useMemo(() => {
        return rolesData?.upercutRoles ?? [];
    }, [rolesData]);

    const openCreateDialog = () => {
        setDialogMode('create');
        setEditingRole(null);
        setDialogOpen(true);
    };

    const openEditDialog = useCallback((role: RoleItem) => {
        setDialogMode('edit');
        setEditingRole(role);
        setDialogOpen(true);
    }, []);

    const handleDelete = useCallback(async (roleId: string) => {
        const confirmed = await confirm({confirmation: 'Êtes-vous sûr de vouloir supprimer ce rôle? Cela peut affecter les utilisateurs qui l\'utilisent.'});
        if (!confirmed) {
            return;
        }
        try {
            await deleteRole({ variables: { id: roleId } });
            showSnackbar('Rôle supprimé.', 'success');
            await refetchRoles();
        } catch (error: any) {
            showSnackbar(error?.message || 'Échec de la suppression du rôle.', 'error');
        }
    }, [deleteRole, showSnackbar, refetchRoles]);

    const roleColumns: DataTableColumn<RoleItem>[] = useMemo(() => [
        {
            id: 'name',
            label: 'Nom',
            field: 'name',
            sortable: true,
        },
        {
            id: 'slug',
            label: 'Slug',
            field: 'slug',
            sortable: true,
        },
        {
            id: 'permissions',
            label: 'Permissions',
            sortable: false,
            width: 300,
            renderCell: ({ row }) => (
                <Typography variant="body2">
                    {row.permissions.map(permission => permission.name).join(', ')}
                </Typography>
            ),
        },
        {
            id: 'actions',
            label: 'Actions',
            sortable: false,
            renderCell: ({ row }) => (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <IconButton variant="contained" sx={{ width: 32, height: 32 }} size="small" color="primary" onClick={() => openEditDialog(row)}>
                        <i className="fi fi-br-pencil" style={{ fontSize: 14 }} ></i>
                    </IconButton>
                    <IconButton disabled={row.isSystemRole} variant="contained" sx={{ width: 32, height: 32 }} size="small" color="error" onClick={() => handleDelete(row._id)}>
                        <i className="fi fi-br-trash" style={{ fontSize: 14 }} ></i>
                    </IconButton>
                </Stack>
            ),
        },
    ], [openEditDialog, handleDelete]);

    const handleSubmit = async (values: { name: string; slug: string; description: string; permissionIds: string[] }) => {
        try {
            if (dialogMode === 'create') {
                await createRole({ variables: { input: values } });
                showSnackbar('Rôle créé.', 'success');
            } else if (editingRole) {
                await updateRole({ variables: { id: editingRole._id, input: values } });
                showSnackbar('Rôle mis à jour.', 'success');
            }
            setDialogOpen(false);
            await refetchRoles();
        } catch (error: any) {
            showSnackbar(error?.message || 'Échec de l\'enregistrement du rôle.', 'error');
        }
    };

    return (
        <>
            <Stack spacing={3} sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <SearchBarWithNumber
                        placeholder="Rechercher par nom, slug ou description..."
                        onChange={(e) => setSearchQuery(e)}
                        totalCount={roles.length}
                    />
                    <Stack direction="row" spacing={1}>
                        <Button
                            startIcon={<i className="fi fi-br-plus" style={{ fontSize: 14 }}></i>}
                            size="large" variant="contained" onClick={openCreateDialog}>
                            Ajouter
                        </Button>
                    </Stack>
                </Stack>
                <DataTable
                    rows={roles}
                    columns={roleColumns}
                    loading={false}
                    height={400}
                />
            </Stack>

            <RoleDialog
                open={dialogOpen}
                mode={dialogMode}
                role={editingRole}
                permissions={permissions}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleSubmit}
            />

        </>
    );
};

export default RoleManagement;
